import { useEffect, useState, type PropsWithChildren } from "react";
import {
  initializeManaged,
  useProductSignIn as returnToProductSignIn,
} from "./managed";

import { SharedLoginContext } from "./SharedLoginContext";

export function ManagedEntry({ children }: PropsWithChildren) {
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [mounted, setMounted] = useState(false);
  const [sharedLoginAvailable, setSharedLoginAvailable] = useState(false);
  const [error, setError] = useState("");
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    let alive = true,
      generation = 0;
    async function check(hide = true) {
      const request = ++generation;
      if (alive && hide) setState("loading");
      try {
        const active = await initializeManaged();
        if (alive && request === generation) {
          setSharedLoginAvailable(active);
          setMounted(true);
          setState("ready");
        }
      } catch (e) {
        if (alive && request === generation) {
          setError(
            e instanceof Error ? e.message : "Business sign-in is unavailable.",
          );
          setState("error");
        }
      }
    }
    const refresh = () => {
      if (sessionStorage.getItem("leadcat.workspace.context.v1")) void check();
    };
    const visible = () => {
      if (!document.hidden) refresh();
    };
    void check();
    window.addEventListener("focus", refresh);
    window.addEventListener("pageshow", refresh);
    document.addEventListener("visibilitychange", visible);
    const channel =
      typeof BroadcastChannel !== "undefined"
        ? new BroadcastChannel("leadcat.identity")
        : null;
    if (channel) channel.onmessage = refresh;
    const timer = window.setInterval(() => {
      if (
        !document.hidden &&
        sessionStorage.getItem("leadcat.workspace.context.v1")
      )
        void check(false);
    }, 30000);
    return () => {
      alive = false;
      generation++;
      window.clearInterval(timer);
      channel?.close();
      window.removeEventListener("focus", refresh);
      window.removeEventListener("pageshow", refresh);
      document.removeEventListener("visibilitychange", visible);
    };
  }, []);
  if (state === "loading" && !mounted)
    return (
      <main role="status" style={{ padding: "2rem" }}>
        Checking your workspace…
      </main>
    );
  if (state === "error")
    return (
      <main style={{ padding: "2rem" }}>
        <h1>Choose your business</h1>
        <p role="alert">{error}</p>
        <a href="https://os.leadcat.in/platform/workspaces/entry">Choose a business in LeadCat OS</a>
        <p>
          <button
            disabled={leaving}
            aria-busy={leaving}
            onClick={async () => {
              setLeaving(true);
              try {
                await returnToProductSignIn();
              } catch (e) {
                setError(e instanceof Error ? e.message : "Please try again.");
                setLeaving(false);
              }
            }}
          >
            {leaving ? "Returning to sign-in…" : "Use product sign-in"}
          </button>
        </p>
      </main>
    );
  return (
    <>
      <div hidden={state !== "ready"}>
        <SharedLoginContext.Provider value={sharedLoginAvailable}>
          {children}
        </SharedLoginContext.Provider>
      </div>
      {state === "loading" && <p role="status">Checking your workspace…</p>}
    </>
  );
}
